import { useEffect, useState } from 'react';
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  createScaffolderFieldExtension,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import {
  AzureSubscription,
  beehiveAzureSubscriptionsApiRef,
} from '../../apis/azureSubscriptions';

export function AzureSubscriptionPicker(
  props: FieldExtensionComponentProps<string | undefined>,
) {
  const {
    disabled,
    formData,
    idSchema,
    onChange,
    rawErrors,
    readonly,
    required,
    schema,
  } = props;
  const subscriptionsApi = useApi(beehiveAzureSubscriptionsApiRef);
  const [subscriptions, setSubscriptions] = useState<AzureSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let mounted = true;

    subscriptionsApi
      .listSubscriptions()
      .then(items => {
        if (!mounted) {
          return;
        }

        setSubscriptions(items);
        setError(undefined);
      })
      .catch(e => {
        if (!mounted) {
          return;
        }

        setSubscriptions([]);
        setError(
          e instanceof Error ? e.message : 'Failed to load subscriptions',
        );
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [subscriptionsApi]);

  useEffect(() => {
    if (subscriptions.length === 1 && formData !== subscriptions[0].id) {
      onChange(subscriptions[0].id);
    }
  }, [formData, onChange, subscriptions]);

  const hasError = Boolean(error) || Boolean(rawErrors?.length);
  const helperText = error ?? rawErrors?.[0] ?? schema.description;
  const label = schema.title ?? 'Subscription';
  const fieldDisabled =
    disabled || readonly || loading || subscriptions.length === 0;

  return (
    <FormControl
      fullWidth
      margin="normal"
      required={required}
      error={hasError}
      disabled={fieldDisabled}
    >
      <InputLabel id={`${idSchema.$id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${idSchema.$id}-label`}
        id={idSchema.$id}
        value={formData ?? ''}
        onChange={event => onChange(event.target.value as string)}
      >
        {subscriptions.map(subscription => (
          <MenuItem key={subscription.id} value={subscription.id}>
            {subscription.name}
          </MenuItem>
        ))}
      </Select>
      {loading && (
        <FormHelperText component="div">
          <CircularProgress size={14} /> Loading Azure subscriptions…
        </FormHelperText>
      )}
      {!loading && subscriptions.length === 0 && !error && (
        <FormHelperText>No Azure subscriptions are configured.</FormHelperText>
      )}
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      {formData && (
        <Typography variant="caption" color="textSecondary">
          Selected subscription ID: {formData}
        </Typography>
      )}
    </FormControl>
  );
}

export const AzureSubscriptionPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension<string | undefined>({
    name: 'AzureSubscriptionPicker',
    component: AzureSubscriptionPicker,
    schema: {
      returnValue: {
        type: 'string',
      },
    },
  }),
);
